import './TransactionsPage.css';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';

import { fetchTransactionHistoryAsync } from '../auth/tokenApi';
import { useLanguage } from '../../hooks/useLanguage';
import en from '../comicForm/translations/en';
import sr from '../comicForm/translations/sr';
import sl from '../comicForm/translations/sl';
import { asIcon } from '../icons';
import AppShell from '../appShell/AppShell';

const BackIcon = asIcon(FaArrowLeft);

interface TransactionHistoryItem {
  amount: number;
  status: string | null;
  source: string;
  dateCreated: string;
}

interface TransactionHistoryResponse {
  content: TransactionHistoryItem[];
  page: number;
  pageSize: number;
  totalElements: number;
}

interface TransactionLabelTranslations {
  statusCompleted: string;
  sourcePurchase: string;
  sourceCoupon: string;
  sourceRefund: string;
  sourceDailyGift: string;
  sourceScicomic: string;
  sourceCite: string;
}

const PAGE_SIZES = [10, 20, 25, 50];

const formatDate = (iso: string) => {
  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, '0');

  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const getStatusLabel = (
  status: string | null,
  translations: TransactionLabelTranslations,
) => {
  if (!status || status.toUpperCase() === 'COMPLETED') {
    return translations.statusCompleted;
  }

  return status;
};

const getSourceLabel = (
  source: string,
  translations: TransactionLabelTranslations,
) => {
  switch (source.toUpperCase()) {
    case 'PURCHASE':
      return translations.sourcePurchase;
    case 'COUPON':
      return translations.sourceCoupon;
    case 'REFUND':
      return translations.sourceRefund;
    case 'DAILY GIFT':
      return translations.sourceDailyGift;
    case 'SCICOMIC':
      return translations.sourceScicomic;
    case 'CITE':
      return translations.sourceCite;
    default:
      return source;
  }
};

const TransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { translations, language, changeLanguage } = useLanguage({
    en,
    sr,
    sl,
  });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState<TransactionHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadTransactions = async () => {
      setIsLoading(true);
      setIsError(false);

      try {
        const response = await fetchTransactionHistoryAsync(page, pageSize);
        if (isMounted) {
          setData(response);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching transaction history:', error);
          setIsError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadTransactions();

    return () => {
      isMounted = false;
    };
  }, [page, pageSize]);

  const totalPages = data
    ? Math.max(1, Math.ceil(data.totalElements / data.pageSize))
    : 1;

  return (
    <AppShell
      page="transactions"
      className="transactions-page"
      translations={translations}
      language={language}
      changeLanguage={changeLanguage}
    >
      <main className="transactions-content">
        <div className="transactions-header">
          <button
            type="button"
            className="transactions-back-link"
            onClick={() => navigate('/comic')}
          >
            <BackIcon size={18} />
          </button>
          <div>
            <h1 className="transactions-title">{translations.transactions}</h1>
            <p className="transactions-subtitle">
              {translations.transactionHistory}
            </p>
          </div>
        </div>

        {isLoading && (
          <div className="transactions-empty-state">
            {translations.redeeming}
          </div>
        )}

        {isError && !isLoading && (
          <div className="transactions-empty-state">
            {translations.couldNotLoadTransactions}
          </div>
        )}

        {!isLoading && !isError && data?.content.length === 0 && (
          <div className="transactions-empty-state">
            {translations.noTransactions}
          </div>
        )}

        {!isLoading && !isError && data && data.content.length > 0 && (
          <div className="transactions-results">
            <div className="transactions-table-wrapper">
              <table className="transactions-table">
                <thead>
                  <tr>
                    <th>{translations.transactionDate}</th>
                    <th>{translations.transactionSource}</th>
                    <th>{translations.transactionAmount}</th>
                    <th>{translations.transactionStatus}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.content.map((transaction, index) => (
                    <tr key={`${transaction.dateCreated}-${index}`}>
                      <td>{formatDate(transaction.dateCreated)}</td>
                      <td>
                        <span className="transaction-source-pill">
                          {getSourceLabel(transaction.source, translations)}
                        </span>
                      </td>
                      <td
                        className={`transaction-amount ${
                          transaction.amount >= 0 ? 'positive' : 'negative'
                        }`}
                      >
                        {transaction.amount >= 0 ? '+' : ''}
                        {transaction.amount}
                      </td>
                      <td>
                        {getStatusLabel(transaction.status, translations)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="transactions-footer">
              <div className="transactions-page-size">
                <span>{translations.rowsPerPage}</span>
                <select
                  value={pageSize}
                  onChange={(event) => {
                    setPageSize(Number(event.target.value));
                    setPage(0);
                  }}
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              <span className="transactions-page-count">
                {translations.pageOf
                  .replace('__PAGE__', String(page + 1))
                  .replace('__TOTAL__', String(totalPages))}
              </span>

              <div className="transactions-pagination">
                <button
                  type="button"
                  onClick={() => setPage((currentPage) => currentPage - 1)}
                  disabled={page === 0}
                >
                  {translations.previous}
                </button>
                <button
                  type="button"
                  onClick={() => setPage((currentPage) => currentPage + 1)}
                  disabled={page >= totalPages - 1}
                >
                  {translations.next}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </AppShell>
  );
};

export default TransactionsPage;
